<template>
  <label :class="classList" tabindex="0">
    <template v-if="name">
      <input
        aria-hidden="false"
        :aria-label="ariaLabel"
        :id="id"
        :type="type"
        :name="name"
        ref="input"
        :checked="checked"
        :value="radioValue"
        :disabled="disabled"
        @change="update" />
      <CircleIcon
        v-if="type === 'radio'"
        class="a-checkbox__icon"
      />
      <CheckIcon
        v-else
        class="a-checkbox__icon"
      />
    </template>
    <template v-else>
      <input
        aria-hidden="false"
        :aria-label="ariaLabel"
        :id="id"
        :type="type"
        ref="input"
        :checked="checked"
        :disabled="disabled"
        @change="update" />
      <CircleIcon
        v-if="type === 'radio'"
        class="a-checkbox__icon"
      />
      <CheckIcon
        v-else
        class="a-checkbox__icon"
      />
      <div class="a-checkbox__toggle__button" v-if="subtype === 'toggle'"></div>
    </template>
  </label>
</template>

<script>
import { CheckIcon, CircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VCheckbox',
  introduction: 'A styled checkbox, toggle or radio.',
  components: {
    CircleIcon,
    CheckIcon,
  },
  props: {
    type: {
      type: String,
      required: true,
      note: 'Either "checkbox" or "radio" and creates the elements accordingly.',
    },
    ariaLabel: {
      type: String,
      required: false,
    },
    id: {
      type: String,
    },
    name: {
      // TODO Should probably be string only
      type: [String, Boolean],
      default: false,
      note: 'If it is defined a radio button is rendered with this value as a name.',
    },
    disabled: {
      type: Boolean,
      default: false,
      note: 'Toggles the checkbox or radio button as disabled.',
    },
    checked: {
      type: Boolean,
      default: false,
      note: 'When it is a checkbox it sets the initial state',
    },
    radioValue: {
      type: String,
      note: 'The value which the radio button name is set to if selected.',
    },
    value: {
      // TODO remove
      type: [String, Number, Boolean],
      note: 'This is not used.',
    },
    subtype: {
      type: String,
      note: 'If "toggle" the checkbox is rendered as a toggle',
    },
    size: {
      type: String,
      note: 'Sets the size of the element (can be "small")',
    },
  },
  data() {
    return {
      isChecked: false,
    };
  },
  created() {
    if (this.checked === true) {
      this.isChecked = true;
    }
  },
  computed: {
    classList() {
      const defaultClass = 'a-checkbox';
      const type = ` a-checkbox--${this.type}`;
      const size = this.size ? ` a-checkbox--${this.size}` : '';
      const subtype = this.subtype ? ` a-checkbox--${this.subtype}` : '';
      const checked = this.isChecked ? ' a-checkbox--checked' : '';
      const disabled = this.disabled ? ' a-checkbox--disabled' : '';
      return defaultClass + type + size + subtype + checked + disabled;
    },
  },
  methods: {
    update() {
      // These should be replaced with v-model into the input elements
      if (this.name) {
        this.$emit('input', this.$refs.input.value);
      } else {
        this.$emit('input', this.$refs.input.checked);
      }
    },
  },
  watch: {
    checked: {
      handler() {
        if (this.checked === true) {
          this.isChecked = true;
        } else {
          this.isChecked = false;
        }
      },
    },
    deep: true,
  },
};
</script>

<style scoped>
.a-checkbox {
  background: var(--signable-secondary-4-shade-3);
  border: 1px solid var(--color-border);
  display: inline-block;
  cursor: pointer;
  height: 24px;
  width: 24px;
  position: relative;
  outline: none;
  box-shadow: inset 0px 1px 5px -1px var(--signable-secondary-4-shade-2);
}

.a-checkbox:hover {
  box-shadow: inset 0 1px 5px 1px var(--signable-secondary-4-shade-2);
}

.a-checkbox input {
  display: none;
}

.a-checkbox input:checked + .a-checkbox__icon {
  display: block;
}

.a-checkbox__icon {
  display: none;
  height: 12px;
  width: 12px;
  color: #555;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
}

.a-checkbox--radio {
  border-radius: 30px;
}

.a-checkbox--radio .a-checkbox__icon {
  height: 12px;
  width: 12px;
}

.a-checkbox--checkbox {
  border-radius: 3px;
}

.a-checkbox--toggle {
  min-width: 44px;
  border-radius: 30px;
  height: 28px;
}

.a-checkbox--toggle input:checked + .a-checkbox__icon {
  display: none;
}

.a-checkbox--toggle input:checked + .a-checkbox__icon + .a-checkbox__toggle__button {
  left: 18px;
}

.a-checkbox--toggle .a-checkbox__toggle__button {
  height: 22px;
  width: 22px;
  top: 2px;
  left: 2px;
  border: 1px solid var(--color-primary-dark);
  background: var(--signable-primary-1);
  position: absolute;
  border-radius: 30px;
  transition: left .25s ease;
}

.a-checkbox--small {
  height: 20px;
  width: 20px;
}

.a-checkbox--small.a-checkbox--toggle {
  height: 20px;
  min-width: 40px;
}

.a-checkbox--small.a-checkbox--toggle input:checked + .a-checkbox__icon {
  display: none;
}

.a-checkbox--small.a-checkbox--toggle input:checked + .a-checkbox__icon + .a-checkbox__toggle__button {
  left: 21px;
}

.a-checkbox--small.a-checkbox--toggle .a-checkbox__toggle__button {
  height: 16px;
  width: 16px;
  top: 1px;
}

.a-checkbox--checked.a-checkbox--toggle {
  background: var(--color-success);
  box-shadow: inset 0 1px 5px 1px var(--color-success);
  border: 1px solid var(--color-success-dark);
}

.a-checkbox--checked.a-checkbox--toggle:hover {
  box-shadow: inset 0 1px 5px 1px var(--color-success-dark);
}

.a-checkbox--checked .a-checkbox__toggle__button {
  background: #fff;
  border: 1px solid var(--color-success-dark);
}

.a-checkbox--disabled {
  cursor: not-allowed;
  background: var(--signable-secondary-4-shade-3);
  box-shadow: none !important;
}

.a-checkbox--disabled .a-checkbox__icon {
  color: var(--signable-secondary-4-shade-1);
}

.a-checkbox--disabled .a-checkbox__toggle__button {
  border: 1px solid #bfc9d0;
  background: #bfc9d0;
}
</style>
